import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {AnimationType, ISheetAPI, SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import bind from 'bind-decorator';

export class AnimationFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'animation';
    }

    @Function({
        title: 'TWEEN(page_name, component_name, options, [repeater])',
        tip: '',
        alias: 'TWEEN',
        example: 'TWEEN("Page 1", "Box 1", A1:C6, 2)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public async tween(@Param({
                           title: 'sheetAPI',
                           tip: 'API',
                           type: SheetType.SheetAPI
                       }) api:ISheetAPI,
                       @Param({
                           title: 'page_name',
                           tip: 'page_name',
                           type: Type.String
                       }) pageName:AString,
                       @Param({
                           title: 'component_name',
                           tip: 'component_name',
                           type: Type.String
                       }) componentName:IValue,
                       @Param({
                           title: 'options',
                           tip: 'options',
                           type: Type.Frame
                       }) options:IFrame,
                       @Param({
                           title: 'repeater',
                           tip: 'repeater',
                           type: SheetType.AnyIValue,
                           optional: true
                       }) repeater:IValue):Promise<ABoolean | AError>
    {
        const result:boolean | Error = await api.getSurfaceContext().animate(
            pageName.toString(),
            componentName.toString(),
            this._gerRepeaterLevels(repeater),
            AnimationType.Tween,
            options
        );

        return typeof result === 'boolean'
            ? ABoolean.fromBoolean(result)
            : AError.fromString(result.message);
    }

    @Function({
        title: 'ANIMATION_STOP(page_name, component_name, [repeater])',
        tip: '',
        alias: 'ANIMATION_STOP',
        example: 'ANIMATION_STOP("Page 1", "Box 1")',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'true',
                tip: '',
                type: Type.Boolean,
            }
    })
    @bind
    public animationStop(@Param({
                             title: 'sheetAPI',
                             tip: 'API',
                             type: SheetType.SheetAPI
                         }) api:ISheetAPI,
                         @Param({
                             title: 'page_name',
                             tip: 'page_name',
                             type: Type.String
                         }) pageName:AString,
                         @Param({
                             title: 'component_name',
                             tip: 'component_name',
                             type: Type.String
                         }) componentName:IValue,
                         @Param({
                             title: 'repeater',
                             tip: 'repeater',
                             type: SheetType.AnyIValue,
                             optional: true
                         }) repeater:IValue):ABoolean
    {

        api.getSurfaceContext().stopAnimations(
            pageName.toString(),
            componentName.toString(),
            this._gerRepeaterLevels(repeater)
        );

        return ABoolean.true();
    }

    private _gerRepeaterLevels(repeater:IValue):Array<number>
    {
        let repeaterLevels:Array<number> = null;

        if( Type.isInstanceOf(repeater, Type.Number) || Type.isInstanceOf(repeater, Type.Integer) )
        {
            repeaterLevels = [(repeater as ANumber).value - 1];
        }
        else if( Type.isInstanceOf(repeater, Type.Frame) )
        {
            const repeaterFrame:IFrame = repeater as IFrame;

            repeaterLevels = repeaterFrame.getAllValues().map((value:IValue) =>
            {
                const result:IValue = CoercionUtils.coerce(value, Type.Integer, AInteger);

                if( result.isEmpty || result.isError )
                    throw Error(EvalErrors.InvalidFunctionArg.error);

                return (result as AInteger).value - 1;
            });
        }

        return repeaterLevels;
    }
}
